import {
  Component,
  computed,
  forwardRef,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { createNumberArray } from '../../../utils/number-array.util';
import { MilitaryTime, toMilitaryTime } from '../../../utils/time.util';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';

@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  imports: [
    DatePipe,
    NgClass,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    DecimalPipe
  ],
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true
    }
  ]
})
export class TimePickerComponent implements ControlValueAccessor {
  protected HOUR_OPTIONS: number[] = createNumberArray(1, 12);
  protected MINUTE_OPTIONS: number[] = createNumberArray(0, 59, 5);
  protected PERIOD_OPTIONS: string[] = ['AM', 'PM'];

  label: InputSignal<string> = input<string>('Time');

  hour: WritableSignal<number | null> = signal<number | null>(null);
  minute: WritableSignal<number | null> = signal<number | null>(null);
  period: WritableSignal<string> = signal<string>('AM');

  selectedTime: Signal<Date | null> = computed(() => {
    const hour = this.hour();
    const minute = this.minute();

    if (hour === null || minute === null) {
      return null;
    }

    const date: Date = new Date(0);
    const militaryTime: MilitaryTime = toMilitaryTime(
      `${hour}:${minute} ${this.period()}`
    );

    date.setHours(militaryTime.hour);
    date.setMinutes(militaryTime.minute);

    return date;
  });

  outline: WritableSignal<boolean> = signal<boolean>(true);

  // State
  isTimePickerOpen: WritableSignal<boolean> = signal<boolean>(false);
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);

  toggleOpen() {
    this.isTimePickerOpen.update(
      (isTimePickerOpen: boolean) => !isTimePickerOpen
    );

    this.onTouch?.();
  }

  closeTimePicker() {
    this.isTimePickerOpen.set(false);
  }

  onPeriodClick(period: string): void {
    this.period.set(period);
    this.onChange?.(this.selectedTime());
    this.onTouch?.();
  }

  onHourClick(hour: number): void {
    this.hour.set(hour);
    this.onChange?.(this.selectedTime());
    this.onTouch?.();
  }

  onMinuteClick(minute: number): void {
    this.minute.set(minute);
    this.onChange?.(this.selectedTime());
    this.onTouch?.();
  }

  // Value Accessor Functions
  private onChange?: (value: Date | unknown) => void;
  private onTouch?: () => void;

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled.set(isDisabled);
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  writeValue(outsideValue?: Date | string): void {
    if (!outsideValue) {
      this.hour.set(null);
      this.minute.set(null);
      return;
    }

    // Handle Date objects
    if (outsideValue instanceof Date) {
      let hour: number = outsideValue.getHours();
      const minute: number = outsideValue.getMinutes();
      const period: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM';

      if (hour === 0) {
        hour = 12;
      } else if (hour > 12) {
        hour -= 12;
      }

      this.hour.set(hour);
      this.minute.set(minute);
      this.period.set(period);
      return;
    }

    // Handle ISO date strings (e.g., "1970-01-01T08:10:00.000Z")
    if (typeof outsideValue === 'string') {
      const isoDateMatch = outsideValue.match(
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
      );
      if (isoDateMatch) {
        const dateObj = new Date(outsideValue);
        if (!isNaN(dateObj.getTime())) {
          // Extract time from the date string
          let hour: number = dateObj.getHours();
          const minute: number = dateObj.getMinutes();
          const period: 'AM' | 'PM' = hour >= 12 ? 'PM' : 'AM';

          if (hour === 0) {
            hour = 12;
          } else if (hour > 12) {
            hour -= 12;
          }

          this.hour.set(hour);
          this.minute.set(minute);
          this.period.set(period);
          return;
        }
      }
    }

    // If we get here, the value is invalid - reset to null
    this.hour.set(null);
    this.minute.set(null);
  }
}
